import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { accessibleDivisionNames } from "../lib/access";
import { zonedWallToUtcIso, utcIsoToZonedWall, tzDisplay } from "../lib/timezone";
import AdvisoryMap from "./AdvisoryMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronDown, ChevronUp, MapPin, Search, X } from "lucide-react";
import { toast } from "sonner";

const defaultReview = (tz) => {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  return utcIsoToZonedWall(d.toISOString(), tz);
};

export default function AdvisoryForm({ open, onClose, onSaved, config, divisions, advisory }) {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const editing = !!advisory;
  const divNames = useMemo(() => accessibleDivisionNames(user, (divisions || []).filter((d) => d.active !== false)), [user, divisions]);
  const [f, setF] = useState({});
  const [more, setMore] = useState(false);
  const [pin, setPin] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const a = advisory || {};
    setF({
      title: a.title || "", category: a.category || "", location_description: a.location_description || "",
      description: a.description || "", next_review_at: a.next_review_at ? utcIsoToZonedWall(a.next_review_at, tz) : defaultReview(tz),
      source: a.source || "", expected_end_at: a.expected_end_at ? utcIsoToZonedWall(a.expected_end_at, tz) : "",
      access_notes: a.access_notes || "", division: a.division || user?.primary_division || divNames[0] || "",
      visibility: a.visibility || "division",
    });
    const hasPin = a.lat != null && a.lng != null;
    setPin(hasPin ? { lat: a.lat, lng: a.lng } : null);
    setShowMap(hasPin);
    setMore(!!(a.source || a.expected_end_at || a.access_notes || hasPin));
    setQ(""); setResults([]);
  }, [open, advisory, tz, user, divNames]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get("/advisories/geocode", { params: { q: q.trim() } });
      setResults(data.results || []);
      if (!(data.results || []).length) toast.message("No matches found. You can place the pin manually.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSearching(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Select a category.");
    const body = {
      title: f.title, category: f.category, location_description: f.location_description, description: f.description,
      next_review_at: zonedWallToUtcIso(f.next_review_at, tz),
      source: f.source, expected_end_at: f.expected_end_at ? zonedWallToUtcIso(f.expected_end_at, tz) : "",
      access_notes: f.access_notes, division: f.division, visibility: f.visibility,
      lat: pin ? pin.lat : null, lng: pin ? pin.lng : null,
      version: advisory?.version ?? null,
    };
    setSaving(true);
    try {
      const { data } = editing ? await api.put(`/advisories/${advisory.id}`, body) : await api.post("/advisories", body);
      toast.success(editing ? "Advisory updated" : "Advisory created");
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="advisory-form-dialog" className="max-h-[92vh] w-[96vw] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black">{editing ? "Edit Advisory" : "Add Advisory"}</DialogTitle>
          <DialogDescription className="text-xs">Persistent operational condition that stays visible across shifts until resolved. Times in {tzDisplay(tz)}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="adv-title">Title *</Label>
            <Input id="adv-title" data-testid="advisory-title-input" required maxLength={120} value={f.title || ""} onChange={set("title")} placeholder="e.g. CR-12 closed at Mill Creek bridge" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Category *</Label>
              <Select value={f.category || ""} onValueChange={set("category")}>
                <SelectTrigger data-testid="advisory-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{(config?.categories || []).map((c) => <SelectItem key={c} value={c} data-testid={`advisory-category-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="adv-review">Next review (date &amp; time) *</Label>
              <Input id="adv-review" data-testid="advisory-review-input" type="datetime-local" required value={f.next_review_at || ""} onChange={set("next_review_at")} />
            </div>
          </div>
          <div>
            <Label htmlFor="adv-loc">Location description *</Label>
            <Input id="adv-loc" data-testid="advisory-location-input" required value={f.location_description || ""} onChange={set("location_description")} placeholder="Road name, intersection, or address" />
          </div>
          <div>
            <Label htmlFor="adv-desc">What is affected *</Label>
            <Textarea id="adv-desc" data-testid="advisory-description-input" required rows={3} value={f.description || ""} onChange={set("description")} placeholder="Brief description of the condition and its impact" />
          </div>

          <button type="button" data-testid="advisory-more-toggle" onClick={() => setMore((m) => !m)} className="flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">
            {more ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Optional details {more ? "" : "(map pin, source, expected end, detour notes)"}
          </button>

          {more && (
            <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Map location</Label>
                  {pin ? (
                    <button type="button" data-testid="advisory-clear-pin" onClick={() => setPin(null)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-600"><X className="h-3 w-3" /> Remove pin</button>
                  ) : (
                    !showMap && config?.map_enabled && <button type="button" data-testid="advisory-show-map" onClick={() => setShowMap(true)} className="text-xs font-semibold text-blue-700">Place pin on map</button>
                  )}
                </div>
                {config?.geocode_enabled && (
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="advisory-geocode-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder="Search an address or place (optional)" />
                    <Button type="button" data-testid="advisory-geocode-search" variant="outline" onClick={search} disabled={searching} className="gap-1 shrink-0"><Search className="h-4 w-4" /> {searching ? "…" : "Search"}</Button>
                  </div>
                )}
                {results.length > 0 && (
                  <ul data-testid="advisory-geocode-results" className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded border border-slate-200 bg-white p-1">
                    {results.map((r, i) => (
                      <li key={i}>
                        <button type="button" data-testid={`advisory-geocode-result-${i}`} onClick={() => { setPin({ lat: r.lat, lng: r.lng }); setShowMap(true); setResults([]); }} className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-blue-50">{r.label}</button>
                      </li>
                    ))}
                  </ul>
                )}
                {(showMap || pin) && (
                  <div className="mt-2 h-64">
                    <AdvisoryMap testid="advisory-pin-map" config={config} pickable pin={pin} onPick={setPin} className="h-64" />
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-500" data-testid="advisory-pin-status">
                  {pin ? `Pin set at ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)} — confirm it matches the real location before saving.` : "No pin — advisory will show as “Location not mapped”. Search is optional; you can always tap the map to place a pin for rural roads."}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="adv-source">Reporting source</Label>
                  <Input id="adv-source" data-testid="advisory-source-input" value={f.source || ""} onChange={set("source")} placeholder="e.g. County Roads, State DOT" />
                </div>
                <div>
                  <Label htmlFor="adv-end">Expected end (estimate)</Label>
                  <Input id="adv-end" data-testid="advisory-expected-end-input" type="datetime-local" value={f.expected_end_at || ""} onChange={set("expected_end_at")} />
                </div>
              </div>
              <div>
                <Label htmlFor="adv-access">Access / detour notes</Label>
                <Textarea id="adv-access" data-testid="advisory-access-notes-input" rows={2} value={f.access_notes || ""} onChange={set("access_notes")} placeholder="Detour route, who can pass, barricade status…" />
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Division</Label>
              <Select value={f.division || ""} onValueChange={set("division")}>
                <SelectTrigger data-testid="advisory-division-select"><SelectValue placeholder="Division" /></SelectTrigger>
                <SelectContent>{divNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visibility</Label>
              <Select value={f.visibility || "division"} onValueChange={set("visibility")}>
                <SelectTrigger data-testid="advisory-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="division">Division only</SelectItem>
                  {config?.can_share_org_wide && <SelectItem value="org" data-testid="advisory-visibility-org">Agency-wide (all divisions)</SelectItem>}
                </SelectContent>
              </Select>
              {!config?.can_share_org_wide && <p className="mt-1 text-[11px] text-slate-500">Only supervisors and admins can share agency-wide.</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="advisory-form-cancel">Cancel</Button>
            <Button type="submit" data-testid="advisory-form-submit" disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">{saving ? "Saving…" : editing ? "Save changes" : "Create advisory"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
